import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { BoletoEligibilityResponse } from '../services/api/boleto.service';

interface BoletoInvoicePDFButtonProps {
  invoiceId: string;
  language?: string;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'download' | 'open';
  createBoleto?: boolean;
  forceCreate?: boolean;
  children?: React.ReactNode;
  onError?: (error: string) => void;
}

const BoletoInvoicePDFButton: React.FC<BoletoInvoicePDFButtonProps> = ({
  invoiceId,
  language = 'pt-br',
  className = 'btn btn-primary',
  style,
  variant = 'open',
  createBoleto = true,
  forceCreate = false,
  children,
  onError
}) => {
  const [eligibility, setEligibility] = useState<BoletoEligibilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const checkEligibility = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.checkBoletoEligibility(invoiceId);
      setEligibility(response);
    } catch (error: any) {
      console.error('Error checking boleto eligibility:', error);
      // Don't show error for eligibility check - just proceed without boleto
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  const generatePDF = async () => {
    try {
      setGenerating(true);

      const shouldIncludeBoleto = eligibility?.eligible || eligibility?.existing_boleto;
      
      if (shouldIncludeBoleto) {
        // Use boleto-enabled PDF generation
        if (variant === 'download') {
          await apiService.downloadInvoiceWithBoletoPDF(
            invoiceId,
            language,
            createBoleto,
            forceCreate,
            undefined, // notification_url - will use default
            `invoice-${invoiceId}-with-boleto.pdf`
          );
        } else {
          await apiService.openInvoiceWithBoletoPDF(
            invoiceId,
            language,
            createBoleto,
            forceCreate
          );
        }
      } else {
        // Fall back to regular PDF generation
        const url = `/api/v1/invoices/${invoiceId}/document/pdf?language=${language}&output_format=pdf`;
        if (variant === 'download') {
          // Create a temporary link to download
          const link = document.createElement('a');
          link.href = `${apiService.getBaseUrl()}${url}`;
          link.download = `invoice-${invoiceId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          window.open(`${apiService.getBaseUrl()}${url}`, '_blank');
        }
      }
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate PDF';
      alert(`Error: ${errorMessage}`);
      onError?.(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const getButtonText = () => {
    if (generating) return 'Generating...';
    if (loading) return 'Loading...';
    
    if (children) return children;
    
    const shouldIncludeBoleto = eligibility?.eligible || eligibility?.existing_boleto;
    const action = variant === 'download' ? 'Download' : 'View';
    
    if (shouldIncludeBoleto) {
      return `🇧🇷 ${action} PDF + Boleto`;
    } else {
      return `📄 ${action} PDF`;
    }
  };

  const getTitle = () => {
    const shouldIncludeBoleto = eligibility?.eligible || eligibility?.existing_boleto;
    const action = variant === 'download' ? 'Download' : 'Open';
    
    if (shouldIncludeBoleto) {
      return `${action} invoice PDF with boleto payment slip included`;
    } else {
      return `${action} invoice PDF (boleto not available for this invoice)`;
    }
  };

  return (
    <button
      className={className}
      style={style}
      onClick={generatePDF}
      disabled={generating || loading}
      title={getTitle()}
    >
      {getButtonText()}
    </button>
  );
};

export default BoletoInvoicePDFButton;